// @ts-check

/**
 * @typedef {import('~blocks/types').ComponentBlock} ComponentBlock
 * @typedef {import('~blocks/types').SciAmBlock} SciAmBlock
 * @typedef {import('~blocks/types').InjectedBlock} InjectedBlock
 * @typedef {import('~blocks/types').ComponentBlocksWithAds} ComponentBlocksWithAds
 * @typedef {import('../injector').LayoutContext} LayoutContext
 */

import { calculateContentWeights } from "../weights";

/**
 * Simple linear ad injection strategy
 *
 * @param {ComponentBlock[]} componentBlocks
 * @param {LayoutContext} context
 * @returns {ComponentBlocksWithAds}
 */
export function linearInjector(
  componentBlocks = [],
  context = { charsPerLine: undefined, contentWidth: undefined },
) {
  const {
    allowCtas = true,
    allowNewsletterCta = true,
    allowRelatedCta = true,
    // allowAds = true,
    // allowBannerAds = true,
    // allowInlineAds = true,
  } = context;

  const debug =
    !!(typeof window !== "undefined" && window.location.search.match(/debug=injector/)) ||
    undefined;
  const items = [];

  // Injection Config
  const maxAds = 20; // Too many ads will crash the page. We can increase this as we clean up our ad stack
  // Block limits
  const earliestInjectedDepth = 2; // Nothing too early in the story
  const latestInjectionIndex = 2; // No ad "right before the last word"

  // Depth limits
  const minDepthBetweenInjections = 1; // Prevent injections from being too close together
  const idealDepthBetweenAds = 1.8; // Prevent ads from being too close together

  // State
  let lastInjectedIndex = 0;
  let lastInjectedType = "unknown";
  let numInjections = 0;
  let numAds = 0;
  let numInternalCtas = 0;
  let depthSinceLastInjected = 0;

  /** @param {InjectedBlock} adOrCta */
  const inject = (adOrCta) => {
    const itemsLength = items.push(adOrCta);
    numInjections++;

    lastInjectedIndex = itemsLength - 1;
    lastInjectedType = adOrCta.type;
    if (lastInjectedType === "cta") {
      numInternalCtas++;
    } else if (lastInjectedType === "ad") {
      numAds++;
    }
  };

  // All info about the block size
  const blockWeights = calculateContentWeights(componentBlocks, context);

  // Smart calculation that tries to count lines but falls back to
  // readtime normalized so a average paragraph readtime is 1 block.
  const blockDepths = blockWeights.map((bw) => bw.depth);

  for (let index = 0; index < componentBlocks.length; index++) {
    const previousComponent = componentBlocks[index - 1];
    const currentComponent = componentBlocks[index];
    // const nextComponent = componentBlocks[index + 1];
    // const prevInjection = items[lastInjectedIndex];

    // Hard Limits
    const notTooManyAds = numAds < maxAds;

    // @TODO - Where do we think about CTAs vs ads
    const isLastInjectable = index === componentBlocks.length - latestInjectionIndex - 1;

    // On short articles, prefer an ad to a CTA
    const isTooLateForCta = index + 3 > componentBlocks.length - latestInjectionIndex;

    // Injection Type Rules
    const canInjectNewsletterCta = allowNewsletterCta && numInjections === 5;
    const canInjectRelatedCta = allowRelatedCta && numInjections === 2;
    const canInjectCta = allowCtas && (canInjectNewsletterCta || canInjectRelatedCta);
    const shouldInjectCta = canInjectCta && !isTooLateForCta;
    const injectionType = shouldInjectCta ? "cta" : "ad";
    const injectionSubtype =
      injectionType === "cta" ? (canInjectNewsletterCta ? "newsletter" : "related") : undefined;

    // Block Rules
    const beforePassed = beforeRulesPass(previousComponent, injectionType);
    const afterPassed = afterRulesPass(currentComponent, injectionType);

    // Depth Rules
    // @TODO simplify
    const blockDepth = blockDepths[index];
    const minDepthPassed = depthSinceLastInjected > minDepthBetweenInjections;
    const idealDepthPassed = depthSinceLastInjected > idealDepthBetweenAds;
    const depthPassed =
      injectionType === "ad" && lastInjectedType === "ad" && !isLastInjectable
        ? idealDepthPassed
        : minDepthPassed;

    // Positional Rules
    const isNotTooEarlyPassed = depthSinceLastInjected + blockDepth >= earliestInjectedDepth; // Depth based
    const isNotTooLatePassed = componentBlocks[index + latestInjectionIndex] !== undefined; // Index based

    const willInject =
      notTooManyAds &&
      isNotTooEarlyPassed &&
      isNotTooLatePassed &&
      beforePassed &&
      afterPassed &&
      depthPassed;

    if (willInject) {
      const num = injectionType === "ad" ? numAds : numInternalCtas;
      let position = `article-${injectionType}-${num + 1}`;

      // AdOps uses this to target ads.
      // It should never change without signoff from AdOps.
      if (injectionType === "ad") {
        position = `article-${numAds + 1}`;
      }

      inject({
        type: injectionType,

        // This is passed as props to the injected component
        block: {
          type: injectionSubtype,
          position,
        },

        debug,
      });
    }

    if (debug) {
      // add debug data to the current component
      currentComponent.data.injectorDebug = {
        position: index,
        type: `${currentComponent.block.type}/${currentComponent.block.tag}`,
        canInject: beforePassed && afterPassed,
        inBounds: isNotTooEarlyPassed && isNotTooLatePassed,
        willInject,
        depthPassed,
        minDepthPassed,
        idealDepthPassed,
        blockDepth,
        depthSinceLastInjected,
        words: currentComponent.data.stats?.words,
        ...blockWeights[index],
        content: (
          currentComponent.block.content ||
          String(
            currentComponent.block.attributes?.["data-source"] ||
              currentComponent.block.attributes?.["data-text"] ||
              "",
          )
        ).slice(0, 25),
      };

      // if we injected something, copy the debug data to the injected item
      if (willInject) {
        items[items.length - 1].debug = currentComponent.data.injectorDebug;
      }
    }

    // Increase by the current block for our next pass
    depthSinceLastInjected = willInject ? 0 : depthSinceLastInjected + blockDepth;

    // Include the actual item
    items.push(currentComponent);
  }

  if (debug) {
    console.table(items.map(({ debug, ...item }) => item.data?.injectorDebug ?? item));
  }

  return items;
}

/**
 * Check the following block to see
 * if its okay to inject something before it.
 * @param {object} block
 * @returns {boolean}
 */
function afterRulesPass({ block, data } = {}, injectionType) {
  // @TODO - move allowBefore and allowAfter back here.
  if (!block || !data) {
    return false;
  }

  // CTAs don't interrupt the narrative flow like ads do
  if (injectionType === "cta" && block.type === "image") return true;
  return !!data?.injector?.allowBefore;
}

/**
 * Check the previous block to see
 * if its okay to inject something after it.
 * @param {object} block
 * @returns {boolean}
 */
function beforeRulesPass({ block, data } = {}, injectionType) {
  // @TODO - move allowBefore and allowAfter back here.
  if (!block || !data) {
    return false;
  }

  return !!data?.injector?.allowAfter;
}
